import React, { useState, useEffect } from 'react';
import { 
  Download,
  Filter,
  TrendingUp,
  BarChart3,
  DollarSign,
  Receipt,
  Users,
  Briefcase,
  PieChart
} from 'lucide-react';
import Breadcrumb from '../../components/Common/Breadcrumb';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import departmentReportsService from '../../services/departmentReportsService';

const DepartmentReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ departments: [] });
  
  // Filter state
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    department: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  useEffect(() => {
    loadFilterOptions();
    loadReportData();
  }, []);

  useEffect(() => {
    loadReportData();
  }, [filters]);

  const loadFilterOptions = async () => {
    try {
      const response = await departmentReportsService.getDepartmentFilterOptions();
      if (response.success) {
        setFilterOptions(response.data);
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const loadReportData = async () => {
    try {
      setLoading(true);
      const response = await departmentReportsService.getDepartmentReports(filters);
      if (response.success) {
        setReportData(response.data);
      } else {
        toast.error(response.message || 'Failed to load department reports');
      }
    } catch (error) {
      console.error('Error loading department reports:', error);
      if (!error._errorHandled) {
        toast.error('Failed to load department reports');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    if (key === 'department') {
      setSelectedDepartment(value || null);
    }
  };

  const resetFilters = () => {
    setFilters({
      startDate: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      department: ''
    });
    setSelectedDepartment(null);
  };

  const exportReport = () => {
    toast.info('Export functionality coming soon');
  };

  const handleDepartmentClick = (departmentName) => {
    setFilters(prev => ({ ...prev, department: departmentName }));
    setSelectedDepartment(departmentName);
  };

  if (loading && !reportData) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading department reports...</p>
        </div>
      </div>
    );
  }

  const summary = reportData?.summary || {};
  const departmentExpenseSummary = reportData?.departmentExpenseSummary || [];
  const departmentMonthlyTrends = reportData?.departmentMonthlyTrends || [];
  const departmentCategoryBreakdown = reportData?.departmentCategoryBreakdown || [];
  const departmentEmployeeBreakdown = reportData?.departmentEmployeeBreakdown || [];
  const departmentExpenseDetails = reportData?.departmentExpenseDetails || [];

  // Filter data for selected department
  const filteredMonthlyTrends = selectedDepartment 
    ? departmentMonthlyTrends.filter(t => t._id.department === selectedDepartment)
    : departmentMonthlyTrends;
  const filteredCategoryBreakdown = selectedDepartment
    ? departmentCategoryBreakdown.filter(c => c._id.department === selectedDepartment)
    : departmentCategoryBreakdown;
  const filteredEmployeeBreakdown = selectedDepartment
    ? departmentEmployeeBreakdown.filter(e => e._id.department === selectedDepartment)
    : departmentEmployeeBreakdown;

  // Calculate max for chart scaling
  const maxDepartmentAmount = Math.max(...departmentExpenseSummary.map(d => d.totalExpenses || 0), 1);
  const maxMonthlyAmount = Math.max(...filteredMonthlyTrends.map(t => t.totalAmount || 0), 1);
  const maxCategoryAmount = Math.max(...filteredCategoryBreakdown.map(c => c.totalAmount || 0), 1);

  // Group monthly trends by department for comparison
  const monthlyTrendsByDept = {};
  filteredMonthlyTrends.forEach(trend => {
    const dept = trend._id.department || 'Unknown';
    if (!monthlyTrendsByDept[dept]) {
      monthlyTrendsByDept[dept] = [];
    }
    monthlyTrendsByDept[dept].push(trend);
  });

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Expenses', path: '/expenses' },
          { label: 'Department Reports', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Department Reports</h1>
          <p>Department-wise expense allocation</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={16} />
            Filters
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={exportReport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div style={{
          background: '#fff',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                Department
              </label>
              <select
                value={filters.department}
                onChange={(e) => handleFilterChange('department', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  background: '#fff'
                }}
              >
                <option value="">All Departments</option>
                {filterOptions.departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
            <button
              onClick={resetFilters}
              style={{
                padding: '8px 16px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                background: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                color: '#495057'
              }}
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Briefcase size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">
            {summary.totalDepartments || 0}
          </div>
          <div className="metric-title">Total Departments</div>
          <div className="metric-subtitle">All departments</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">
            {formatCurrency(summary.totalExpenses)}
          </div>
          <div className="metric-title">Total Expenses</div>
          <div className="metric-subtitle">All expense claims</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">
            {summary.totalEmployees || 0}
          </div>
          <div className="metric-title">Total Employees</div>
          <div className="metric-subtitle">All employees</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Average</span>
            </div>
          </div>
          <div className="metric-value">
            {formatCurrency(summary.avgExpensePerDepartment)}
          </div>
          <div className="metric-title">Avg per Department</div>
          <div className="metric-subtitle">Per department average</div>
        </div>
      </div>

      {/* Department Expense Summary */}
      <div style={{
        background: '#fff',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
          Department Expense Summary
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Department</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Total Expenses</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Approved</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Pending</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Rejected</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Claims</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Employees</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Avg Claim</th>
              </tr>
            </thead>
            <tbody>
              {departmentExpenseSummary.length > 0 ? (
                departmentExpenseSummary.map((dept, index) => {
                  const employeeCount = dept.totalEmployees ? dept.totalEmployees.length : 0;
                  const width = (dept.totalExpenses / maxDepartmentAmount) * 100;
                  return (
                    <tr 
                      key={index} 
                      style={{ 
                        borderBottom: '1px solid #f1f3f4',
                        cursor: 'pointer',
                        background: selectedDepartment === dept._id ? '#f8f9fa' : 'transparent'
                      }}
                      onClick={() => handleDepartmentClick(dept._id)}
                    >
                      <td style={{ padding: '12px', fontSize: '14px', color: '#495057', fontWeight: '500' }}>
                        {dept._id || 'Unknown'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right', fontWeight: '600' }}>
                        {formatCurrency(dept.totalExpenses)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#43e97b', textAlign: 'right' }}>
                        {formatCurrency(dept.approvedAmount)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#4facfe', textAlign: 'right' }}>
                        {formatCurrency(dept.pendingAmount)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#f5576c', textAlign: 'right' }}>
                        {formatCurrency(dept.rejectedAmount)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right' }}>
                        {dept.totalClaims}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right' }}>
                        {employeeCount}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right' }}>
                        {formatCurrency(dept.avgClaimAmount)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                    No department expense data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Charts Section */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '24px', marginBottom: '24px' }}>
        {/* Monthly Trends Chart */}
        <div style={{
          background: '#fff',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
            Monthly Expense Trends {selectedDepartment && `- ${selectedDepartment}`}
          </h3>
          <div style={{ height: '300px', display: 'flex', alignItems: 'flex-end', gap: '8px', paddingBottom: '40px' }}>
            {filteredMonthlyTrends.length > 0 ? (
              filteredMonthlyTrends.map((trend, index) => {
                const height = (trend.totalAmount / maxMonthlyAmount) * 100;
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                return (
                  <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                      width: '100%',
                      height: `${height}%`,
                      background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '4px 4px 0 0',
                      minHeight: '4px',
                      cursor: 'pointer'
                    }}
                    title={formatCurrency(trend.totalAmount)}
                    ></div>
                    <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--color-text-secondary, #5B6478)', textAlign: 'center' }}>
                      {monthNames[trend._id.month - 1]}
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ width: '100%', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)', padding: '40px' }}>
                No data available
              </div>
            )}
          </div>
        </div>

        {/* Category Breakdown */}
        <div style={{
          background: '#fff',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
            Category Breakdown {selectedDepartment && `- ${selectedDepartment}`}
          </h3>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {filteredCategoryBreakdown.length > 0 ? (
              filteredCategoryBreakdown.map((category, index) => {
                const width = (category.totalAmount / maxCategoryAmount) * 100;
                const colors = [
                  'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  'linear-gradient(90deg, #f093fb 0%, #f5576c 100%)',
                  'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)',
                  'linear-gradient(90deg, #43e97b 0%, #38f9d7 100%)',
                  'linear-gradient(90deg, #fa709a 0%, #fee140 100%)'
                ];
                return (
                  <div key={index} style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-brand-navy, #222448)' }}>
                        {category._id.categoryName || 'Unknown'}
                      </span>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
                        {formatCurrency(category.totalAmount)}
                      </span>
                    </div>
                    <div style={{
                      width: '100%',
                      height: '8px',
                      background: '#e9ecef',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${width}%`,
                        height: '100%',
                        background: colors[index % colors.length],
                        borderRadius: '4px'
                      }}></div>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '4px' }}>
                      {category.itemCount} items
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)', padding: '40px' }}>
                No data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Employee Breakdown by Department */}
      {filteredEmployeeBreakdown.length > 0 && (
        <div style={{
          background: '#fff',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
            Employee Breakdown {selectedDepartment && `- ${selectedDepartment}`}
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Department</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Employee</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Total Expenses</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Claims</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployeeBreakdown.map((emp, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #f1f3f4' }}>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057' }}>
                      {emp._id.department || 'Unknown'}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057', fontWeight: '500' }}>
                      {emp._id.employeeName} ({emp._id.employeeCode})
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right', fontWeight: '600' }}>
                      {formatCurrency(emp.totalExpenses)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right' }}>
                      {emp.totalClaims}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Department Expense Details Table */}
      <div style={{
        background: '#fff',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        padding: '24px'
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
          Recent Expense Claims {selectedDepartment && `- ${selectedDepartment}`}
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Claim Number</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Employee</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Department</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Amount</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {departmentExpenseDetails.length > 0 ? (
                departmentExpenseDetails.map((expense, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #f1f3f4' }}>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057' }}>{expense.claimNumber}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057' }}>{expense.employeeName}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057' }}>{expense.department || '-'}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057' }}>{formatDate(expense.claimDate)}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right', fontWeight: '600' }}>
                      {formatCurrency(expense.grandTotal)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: expense.status === 'Approved' ? '#d4edda' : 
                                   expense.status === 'Rejected' ? '#f8d7da' : 
                                   expense.status === 'Submitted' ? '#d1ecf1' : '#fff3cd',
                        color: expense.status === 'Approved' ? '#155724' : 
                               expense.status === 'Rejected' ? '#721c24' : 
                               expense.status === 'Submitted' ? '#0c5460' : '#856404'
                      }}>
                        {expense.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                    No expense claims found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DepartmentReports;

